import React from 'react';
import { getBackendBoolean } from 'utils/fm';
import ReadOnlyTooltipContent from 'components/ReadOnlyIndicator/ReadOnlyTooltipContent';

const READONLY_OPTION_KEYS = ['edit', 'delete', 'merge'];

/**
 * Normalizes the `isReadOnly` flag across entities. Accepts the detail data object
 * that web4 models expose after `toDetail` / `standardToCrud` (isReadOnly,
 * readOnlyCompany, readOnly).
 */
export const isRecordReadOnly = (data) =>
    getBackendBoolean(data?.isReadOnly) ||
    getBackendBoolean(data?.readOnlyCompany) ||
    getBackendBoolean(data?.readOnly);

/**
 * Returns the detail-header `options` list with `edit`/`delete`/`merge` converted
 * into `{ id, disabled, tooltip }` objects when the user cannot edit the record,
 * and ensures `edit` is always present so the button remains visible (GOKU-11242).
 *
 * When no disable condition applies, the original options array is returned as-is.
 */
export const getReadOnlyOptions = ({
    options,
    data,
    crudPermissions,
    isPublicCompany = false,
    extraDisable = false,
}) => {
    if (!options) return options;

    const canEdit = !!crudPermissions?.update;
    const isReadOnly = isRecordReadOnly(data);
    const isPublic = isPublicCompany && getBackendBoolean(data?.publicCompany);
    const forcedDisable = !canEdit || isReadOnly || extraDisable;

    if (!forcedDisable) return options;

    const result = [...options];
    if (!result.includes('edit')) {
        const deleteIdx = result.findIndex((option) => option === 'delete');
        if (deleteIdx !== -1) result.splice(deleteIdx, 0, 'edit');
        else result.push('edit');
    }

    const tooltip = <ReadOnlyTooltipContent isPublicCompany={isPublic} />;

    return result.map((option) =>
        READONLY_OPTION_KEYS.includes(option) ? { id: option, disabled: true, tooltip } : option,
    );
};

export default getReadOnlyOptions;
